import {
    getHowToWinBannerDismissedCookie,
    getHowToWinHeaderPopoverDismissedCookie,
    setHowToWinHeaderPopoverDismissedCookie,
} from '@/redux/modules/cookies';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import { HowToWinHeaderPopover } from './components/HowToWinHeaderPopover/HowToWinHeaderPopover';
import { isCompleteAccountModalOpen } from '@/redux/modules/account/shouldLogin/shouldLogin.selectors';
import { modalTypes } from '@/enums';
import { resolvePopoverAnchor, ViewportType } from './resolvePopoverAnchor';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHowToWinBannerExperiment } from '@/utils/amplitude/experiments/hooks/useHowToWinBannerExperiment.experiment';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import useIsWiderThan from '@/hooks/useIsWiderThan';

const TABLET_BREAKPOINT_PX = 954;
const WIDE_DESKTOP_BREAKPOINT_PX = 1400;

export const HowToWinHeaderPrompt = () => {
    const dispatch = useAppDispatch();
    const isMobile = useIsNarrowerThan(TABLET_BREAKPOINT_PX);
    const isWideDesktop = useIsWiderThan(WIDE_DESKTOP_BREAKPOINT_PX);

    const viewport: ViewportType = useMemo(() => {
        if (isMobile) {
            return ViewportType.Mobile;
        }
        if (isWideDesktop) {
            return ViewportType.WideDesktop;
        }
        return ViewportType.NarrowDesktop;
    }, [isMobile, isWideDesktop]);
    const isInTreatment = useHowToWinBannerExperiment();
    const hasClosedBanner = useAppSelector(getHowToWinBannerDismissedCookie);
    const hasDismissedPopover = useAppSelector(getHowToWinHeaderPopoverDismissedCookie);
    const openModal = useAppSelector(getOpenModal);
    const completeAccountModalOpen = useAppSelector(isCompleteAccountModalOpen);

    const [showWidget, setShowWidget] = useState(false);
    const [resolvedAnchor, setResolvedAnchor] = useState<ReturnType<typeof resolvePopoverAnchor>>(null);

    const canAttemptShow =
        isInTreatment && !hasDismissedPopover && openModal === modalTypes.NONE && !completeAccountModalOpen;

    const tryShowPopover = useCallback(() => {
        const anchor = resolvePopoverAnchor(viewport);
        if (anchor) {
            setResolvedAnchor(anchor);
            setShowWidget(true);
        }
    }, [viewport]);

    const handleSetCookie = useCallback(() => {
        dispatch(setHowToWinHeaderPopoverDismissedCookie());
    }, [dispatch]);

    const onClose = useCallback(() => {
        setResolvedAnchor(null);
        setShowWidget(false);
        handleSetCookie();
    }, [handleSetCookie]);

    useEffect(() => {
        const handleBannerDismissed = () => {
            if (!canAttemptShow) {
                return;
            }
            tryShowPopover();
        };

        window.addEventListener('howToWinBannerDismissed', handleBannerDismissed);
        return () => window.removeEventListener('howToWinBannerDismissed', handleBannerDismissed);
    }, [canAttemptShow, tryShowPopover]);

    useEffect(() => {
        if (!canAttemptShow || !hasClosedBanner) {
            return;
        }
        tryShowPopover();
    }, [canAttemptShow, hasClosedBanner, tryShowPopover]);

    const canShowWidget = canAttemptShow && hasClosedBanner;

    if (!canShowWidget || !showWidget || !resolvedAnchor) {
        return null;
    }

    return (
        <HowToWinHeaderPopover
            anchorElement={resolvedAnchor.element}
            anchorSelector={resolvedAnchor.selector}
            onClose={onClose}
            onOutsideClick={onClose}
            showWidget={showWidget}
        />
    );
};
